"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { SortDirection } from "@/entities/ISort";
import type { ApiXTdhGrant } from "@/generated/models/ApiXTdhGrant";
import type { ApiXTdhGrantsPage } from "@/generated/models/ApiXTdhGrantsPage";
import { ApiXTdhGrantStatus } from "@/generated/models/ApiXTdhGrantStatus";
import { commonApiFetch } from "@/services/api/common-api";
type XtdhGrantSortField = "created_at" | "valid_from" | "valid_to" | "rate";
interface UseXtdhGrantsSearchQueryParams {
  readonly grantor?: string | null | undefined;
  readonly targetCollectionName?: string | null | undefined;
  readonly targetContract?: string | null | undefined;
  readonly statuses?: readonly ApiXTdhGrantStatus[] | undefined;
  readonly sortField?: XtdhGrantSortField | undefined;
  readonly sortDirection?: SortDirection | undefined;
  readonly pageSize?: number | undefined;
  readonly enabled?: boolean | undefined;
}
type XtdhGrantsInfiniteData = InfiniteData<ApiXTdhGrantsPage>;
type UseXtdhGrantsSearchQueryResult = UseInfiniteQueryResult<XtdhGrantsInfiniteData, Error> & {
  readonly grants: ApiXTdhGrant[];
  readonly totalCount: number;
  readonly errorMessage?: string | undefined;
  readonly isEnabled: boolean;
  readonly firstPage?: ApiXTdhGrantsPage | undefined;
};
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 2000;
const DEFAULT_SORT_FIELD: XtdhGrantSortField = "created_at";
const DEFAULT_SORT_DIRECTION: SortDirection = SortDirection.DESC;
const DEFAULT_STALE_TIME = 30_000; // 30 seconds
const XTDH_GRANT_STATUS_SORT_RANK: Readonly<Record<string, number>> = {
  [ApiXTdhGrantStatus.Pending]: 0,
  [ApiXTdhGrantStatus.Failed]: 1,
  [ApiXTdhGrantStatus.Disabled]: 2,
  [ApiXTdhGrantStatus.Granted]: 3
};
const compareXtdhGrantStatuses = (left: ApiXTdhGrantStatus, right: ApiXTdhGrantStatus): number => {
  const leftRank = XTDH_GRANT_STATUS_SORT_RANK[left];
  const rightRank = XTDH_GRANT_STATUS_SORT_RANK[right];
  if (leftRank !== undefined && rightRank !== undefined) {
    return leftRank - rightRank;
  }
  if (leftRank !== undefined) {
    return -1;
  }
  if (rightRank !== undefined) {
    return 1;
  }
  return left.localeCompare(right);
};
const normalizeTextFilter = (value?: string | null): string => {
  const normalized = value?.trim() ?? "";
  return normalized.length ? normalized : "";
};
const normalizeStatuses = (statuses?: readonly ApiXTdhGrantStatus[]): ApiXTdhGrantStatus[] => {
  if (statuses === undefined || statuses.length === 0) {
    return [];
  }
  return Array.from(new Set(statuses)).sort(compareXtdhGrantStatuses);
};
export function useXtdhGrantsSearchQuery(t0) {
  const $ = _c(37);
  const {
    grantor,
    targetCollectionName,
    targetContract,
    statuses,
    sortField: t1,
    sortDirection: t2,
    pageSize: t3,
    enabled: t4
  } = t0;
  const sortField = t1 === undefined ? DEFAULT_SORT_FIELD : t1;
  const sortDirection = t2 === undefined ? DEFAULT_SORT_DIRECTION : t2;
  const pageSize = t3 === undefined ? DEFAULT_PAGE_SIZE : t3;
  const enabled = t4 === undefined ? true : t4;
  let t5;
  if ($[0] !== grantor) {
    t5 = normalizeTextFilter(grantor);
    $[0] = grantor;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const normalizedGrantor = t5;
  let t6;
  if ($[2] !== targetCollectionName) {
    t6 = normalizeTextFilter(targetCollectionName);
    $[2] = targetCollectionName;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const normalizedTargetCollectionName = t6;
  let t7;
  if ($[4] !== targetContract) {
    t7 = normalizeTextFilter(targetContract);
    $[4] = targetContract;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const normalizedTargetContract = t7;
  let t8;
  if ($[6] !== statuses) {
    const normalizedStatuses = normalizeStatuses(statuses);
    t8 = normalizedStatuses.join(",");
    $[6] = statuses;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const serializedStatuses = t8;
  const normalizedPageSize = Math.min(MAX_PAGE_SIZE, Math.max(1, Math.floor(pageSize)));
  const isEnabled = enabled;
  const t9 = serializedStatuses || "ALL";
  let t10;
  if ($[8] !== normalizedGrantor || $[9] !== normalizedPageSize || $[10] !== normalizedTargetCollectionName || $[11] !== normalizedTargetContract || $[12] !== sortDirection || $[13] !== sortField || $[14] !== t9) {
    t10 = [QueryKey.TDH_GRANTS, "group-create-grants-search", normalizedGrantor, normalizedTargetCollectionName, normalizedTargetContract, t9, sortField, sortDirection, normalizedPageSize];
    $[8] = normalizedGrantor;
    $[9] = normalizedPageSize;
    $[10] = normalizedTargetCollectionName;
    $[11] = normalizedTargetContract;
    $[12] = sortDirection;
    $[13] = sortField;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const queryKey = t10;
  let t11;
  if ($[16] !== normalizedGrantor || $[17] !== normalizedPageSize || $[18] !== normalizedTargetCollectionName || $[19] !== normalizedTargetContract || $[20] !== serializedStatuses || $[21] !== sortDirection || $[22] !== sortField) {
    t11 = async t12 => {
      const {
        pageParam
      } = t12;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      const params = {
        page: currentPage.toString(),
        page_size: normalizedPageSize.toString(),
        sort: sortField,
        sort_direction: sortDirection
      };
      if (normalizedGrantor) {
        params.grantor = normalizedGrantor;
      }
      if (normalizedTargetCollectionName) {
        params.target_collection_name = normalizedTargetCollectionName;
      }
      if (normalizedTargetContract) {
        params.target_contract = normalizedTargetContract;
      }
      if (serializedStatuses) {
        params.status = serializedStatuses;
      }
      return await commonApiFetch({
        endpoint: "xtdh/grants",
        params
      });
    };
    $[16] = normalizedGrantor;
    $[17] = normalizedPageSize;
    $[18] = normalizedTargetCollectionName;
    $[19] = normalizedTargetContract;
    $[20] = serializedStatuses;
    $[21] = sortDirection;
    $[22] = sortField;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== isEnabled || $[25] !== queryKey || $[26] !== t11) {
    t12 = {
      queryKey,
      queryFn: t11,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled: isEnabled,
      staleTime: DEFAULT_STALE_TIME,
      placeholderData: keepPreviousData
    };
    $[24] = isEnabled;
    $[25] = queryKey;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const query = useInfiniteQuery(t12);
  const firstPage = query.data?.pages[0];
  let t13;
  if ($[28] !== query.data?.pages) {
    t13 = query.data?.pages.flatMap(_temp2) ?? [];
    $[28] = query.data?.pages;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const grants = t13;
  const totalCount = firstPage?.count ?? 0;
  const errorMessage = query.error instanceof Error ? query.error.message : undefined;
  let t14;
  if ($[30] !== errorMessage || $[31] !== firstPage || $[32] !== grants || $[33] !== isEnabled || $[34] !== query || $[35] !== totalCount) {
    t14 = {
      ...query,
      grants,
      totalCount,
      errorMessage,
      isEnabled,
      firstPage
    };
    $[30] = errorMessage;
    $[31] = firstPage;
    $[32] = grants;
    $[33] = isEnabled;
    $[34] = query;
    $[35] = totalCount;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function _temp2(pageData) {
  return pageData.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}